/**
 * Brazilian income-tax and IOF rules for fixed-income investments with
 * REGRESSIVE_IR_IOF tax regime (e.g. Tesouro Direto).
 *
 * IR regressivo (Lei 11.196/2005) is applied on the trading gain according to
 * the holding period. IOF (Decreto 6.306/2007) applies only within the first
 * 30 days, decreasing linearly on the published table. Coupon payments are
 * taxed at a flat 15% withheld at payment.
 */

const MS_PER_DAY = 86_400_000;

export const COUPON_IR_RATE = 0.15;

export const holdingPeriodDays = (purchase: Date, sale: Date): number =>
  Math.max(0, Math.round((sale.getTime() - purchase.getTime()) / MS_PER_DAY));

export const regressiveIrRate = (holdingDays: number): number => {
  if (holdingDays <= 180) return 0.225;
  if (holdingDays <= 360) return 0.2;
  if (holdingDays <= 720) return 0.175;
  return 0.15;
};

/** IOF rate on the gain; zero from the 30th day of holding onward. */
export const iofRate = (holdingDays: number): number => {
  if (holdingDays >= 30) return 0;
  return Math.max(0, (0.96 * (30 - holdingDays)) / 30);
};

export type GainTaxBreakdown = {
  holdingDays: number;
  irRate: number;
  iofRate: number;
  irAmount: number;
  iofAmount: number;
  totalTax: number;
};

/** Taxes due on a positive trading gain; a loss yields zero tax. */
export const gainTax = (purchase: Date, sale: Date, gain: number): GainTaxBreakdown => {
  const holdingDays = holdingPeriodDays(purchase, sale);
  const taxableGain = Math.max(0, gain);
  const irRate = regressiveIrRate(holdingDays);
  const iofRateValue = iofRate(holdingDays);
  const irAmount = taxableGain * irRate;
  const iofAmount = taxableGain * iofRateValue;
  return {
    holdingDays,
    irRate,
    iofRate: iofRateValue,
    irAmount,
    iofAmount,
    totalTax: irAmount + iofAmount,
  };
};
